/**
 * InstallAppBanner · v2.7.0 batch DA
 * ====================================
 * Universal PWA install prompt covering every major browser/OS combo:
 *
 *   - Chromium one-tap (Chrome / Edge / Opera / Brave / Samsung Internet,
 *     mobile + desktop) — captures `beforeinstallprompt` and triggers
 *     install on tap.
 *   - Safari iOS / Chrome iOS / Firefox iOS / Edge iOS (all WebKit) →
 *     3-step illustrated guide (Share → Add to Home Screen → Add).
 *   - Safari macOS 14+ → File → Share → Add to Dock guide.
 *   - Firefox Android → ⋮ menu → Install guide.
 *   - Firefox Desktop, IE, anything else → banner hides (no PWA support).
 *   - Already installed (display-mode: standalone) → hides.
 *   - Dismissed in last 7 days → hides.
 */
import { useEffect, useState, useCallback } from "react";
import { Smartphone, X, Share, Plus, Download, MoreVertical, Monitor } from "lucide-react";

const DISMISS_KEY = "buller_pwa_install_banner_dismissed_v1";
const DISMISS_DAYS = 7;

// ---- Browser / OS detection -------------------------------------------------
function getEnv() {
  if (typeof navigator === "undefined") return { mode: "unsupported" };
  const ua = navigator.userAgent || "";
  const lower = ua.toLowerCase();

  // iOS family (Safari, Chrome iOS = CriOS, Firefox iOS = FxiOS, Edge iOS = EdgiOS)
  // All iOS browsers are forced to use WebKit so the install flow is identical.
  if (/iphone|ipad|ipod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) {
    return { mode: "ios", os: "ios" };
  }

  // Firefox Android — manual menu install only, no beforeinstallprompt
  if (/android/i.test(ua) && /firefox/i.test(ua)) {
    return { mode: "firefox-android", os: "android" };
  }

  // Android Chromium family — beforeinstallprompt + manual menu fallback
  if (/android/i.test(ua)) {
    return { mode: "chromium-android", os: "android" };
  }

  // macOS Safari (desktop) — Add to Dock since macOS 14
  if (/macintosh|mac os x/i.test(ua) && /safari/i.test(ua) && !/chrome|chromium|crios|edg|opr/i.test(lower)) {
    return { mode: "macos-safari", os: "macos" };
  }

  // Firefox Desktop — no PWA install support, hide
  if (!/android|iphone|ipad/i.test(ua) && /firefox/i.test(ua)) {
    return { mode: "unsupported", os: "desktop-firefox" };
  }

  // Desktop Chromium (Chrome, Edge, Opera, Brave) — beforeinstallprompt
  if (/chrome|chromium|edg|opr/i.test(lower)) {
    return { mode: "chromium-desktop", os: "desktop" };
  }

  return { mode: "unsupported", os: "unknown" };
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.navigator?.standalone === true) return true;            // iOS
  return window.matchMedia?.("(display-mode: standalone)")?.matches === true
      || window.matchMedia?.("(display-mode: minimal-ui)")?.matches === true
      || window.matchMedia?.("(display-mode: fullscreen)")?.matches === true;
}

function dismissedRecently() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "", 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

// ---- Mode-specific copy + step lists ---------------------------------------
const COPY = {
  "ios":               { title: "Install the Buller Taxi App", sub: "Add to your iPhone home screen for one-tap booking", cta: "How to install" },
  "macos-safari":      { title: "Add Buller Taxi to your Dock", sub: "One-click access from your Mac dock — works like a native app",   cta: "How to add" },
  "chromium-android":  { title: "Install the Buller Taxi App", sub: "Add to your home screen for one-tap booking",            cta: "Install" },
  "chromium-desktop":  { title: "Install the Buller Taxi App", sub: "Pin to your taskbar or app launcher for instant access", cta: "Install" },
  "firefox-android":   { title: "Install the Buller Taxi App", sub: "Add to your Android home screen for one-tap booking",   cta: "How to install" },
};

function IosSteps() {
  return (
    <ol className="space-y-1.5 ml-1">
      <Step n={1}>Tap the <Share className="inline w-4 h-4 mx-0.5 -mt-0.5 text-amber-400"/> <b>Share</b> icon at the bottom of the browser</Step>
      <Step n={2}>Scroll down and tap <Plus className="inline w-4 h-4 mx-0.5 -mt-0.5 text-amber-400"/> <b>"Add to Home Screen"</b></Step>
      <Step n={3}>Tap <b>"Add"</b> in the top-right corner — done!</Step>
    </ol>
  );
}

function MacSafariSteps() {
  return (
    <ol className="space-y-1.5 ml-1">
      <Step n={1}>Click the <Share className="inline w-4 h-4 mx-0.5 -mt-0.5 text-amber-400"/> <b>Share</b> icon in the Safari toolbar (or open the <b>File</b> menu)</Step>
      <Step n={2}>Click <Plus className="inline w-4 h-4 mx-0.5 -mt-0.5 text-amber-400"/> <b>"Add to Dock"</b> → confirm — done!</Step>
    </ol>
  );
}

function FirefoxAndroidSteps() {
  return (
    <ol className="space-y-1.5 ml-1">
      <Step n={1}>Tap the <MoreVertical className="inline w-4 h-4 mx-0.5 -mt-0.5 text-amber-400"/> <b>menu</b> button (3 dots, top-right)</Step>
      <Step n={2}>Tap <b>"Install"</b> (Firefox 79+) or <b>"Add to Home screen"</b></Step>
      <Step n={3}>Confirm — the Buller Taxi icon will appear on your home screen.</Step>
    </ol>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex items-start gap-2">
      <span className="w-5 h-5 rounded-full bg-amber-400 text-zinc-900 text-[11px] font-bold flex items-center justify-center flex-shrink-0">{n}</span>
      <span>{children}</span>
    </li>
  );
}

// ---- Component --------------------------------------------------------------
export default function InstallAppBanner() {
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (isStandalone() || dismissedRecently()) return undefined;
    const env = getEnv();
    if (env.mode === "unsupported") return undefined;

    if (env.mode === "ios" || env.mode === "macos-safari" || env.mode === "firefox-android") {
      // Manual install flows — show banner immediately
      setMode(env.mode);
      setShow(true);
      return undefined;
    }

    // Chromium (mobile + desktop) — wait for beforeinstallprompt for one-tap.
    // Show the banner regardless after 1.2s in case the browser already
    // installed the PWA criteria check OR fired the event before we attached.
    const onBip = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setMode(env.mode);
      setShow(true);
    };
    const onInstalled = () => {
      setShow(false);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    const fallback = setTimeout(() => {
      if (!isStandalone() && !dismissedRecently()) {
        setMode((prev) => prev || env.mode);
        setShow((prev) => prev || true);
      }
    }, 1200);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
      clearTimeout(fallback);
    };
  }, []);

  const manualInstall = useCallback(() => setExpanded((v) => !v), []);

  const oneTapInstall = useCallback(async () => {
    if (!deferredPrompt) {
      // Browser hasn't given us the event yet — fall back to expanded guide
      setExpanded(true);
      return;
    }
    setInstalling(true);
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === "accepted") {
        setShow(false);
      }
    } catch {
      /* user cancelled — keep banner so they can retry */
    } finally {
      setInstalling(false);
      setDeferredPrompt(null);
    }
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); }
    catch { /* private mode — session-only dismiss */ }
    setShow(false);
  }, []);

  if (!show || !mode) return null;

  const copy = COPY[mode] || COPY["chromium-android"];
  const isOneTap = mode === "chromium-android" || mode === "chromium-desktop";
  const Icon = isOneTap ? Download : (mode === "macos-safari" ? Monitor : Smartphone);
  let Steps = IosSteps;
  if (mode === "macos-safari") Steps = MacSafariSteps;
  if (mode === "firefox-android") Steps = FirefoxAndroidSteps;

  return (
    <div
      role="region"
      aria-label="Install Buller Taxi app"
      data-testid="pwa-install-banner"
      data-pwa-mode={mode}
      className="w-full bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-300 text-zinc-900 shadow-md relative z-[55]"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-5 py-2 flex items-center gap-2 sm:gap-3">
        <img
          src="/buller-taxi-app-icon-192.png"
          alt="Buller Taxi app icon"
          width={36}
          height={36}
          className="w-9 h-9 rounded-lg shadow-md flex-shrink-0 border-2 border-zinc-900/15"
        />

        <div className="flex-1 min-w-0">
          <div className="text-xs sm:text-sm font-bold leading-tight">{copy.title}</div>
          <div className="text-[10px] sm:text-xs leading-tight opacity-80 truncate">{copy.sub}</div>
        </div>

        <button
          type="button"
          onClick={isOneTap ? oneTapInstall : manualInstall}
          disabled={installing}
          data-testid={isOneTap ? "pwa-install-one-tap" : "pwa-install-show-steps"}
          className="flex items-center gap-1.5 bg-zinc-900 text-amber-400 px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider shadow hover:bg-zinc-800 transition flex-shrink-0 disabled:opacity-50"
        >
          <Icon className="w-3.5 h-3.5"/>
          <span>{installing ? "Installing…" : copy.cta}</span>
        </button>

        <button
          type="button"
          onClick={dismiss}
          data-testid="pwa-install-dismiss"
          aria-label="Dismiss install banner"
          className="text-zinc-900/60 hover:text-zinc-900 p-1 flex-shrink-0"
        ><X className="w-4 h-4"/></button>
      </div>

      {/* Expanded manual-install instructions */}
      {expanded && (
        <div
          className="bg-zinc-950 text-zinc-100 border-t border-amber-300/30"
          data-testid="pwa-install-steps"
        >
          <div className="max-w-7xl mx-auto px-4 py-3 text-xs sm:text-sm space-y-2">
            <div className="font-bold text-amber-400 uppercase tracking-wider text-[11px]">
              {mode === "ios" && "Three taps to install on iPhone:"}
              {mode === "macos-safari" && "Two clicks to add to your Mac dock:"}
              {mode === "firefox-android" && "Three taps to install in Firefox:"}
              {(mode === "chromium-android" || mode === "chromium-desktop") && "If the install button didn't appear, do this:"}
            </div>
            {(mode === "chromium-android" || mode === "chromium-desktop") ? (
              <ol className="space-y-1.5 ml-1">
                <Step n={1}>Tap the <MoreVertical className="inline w-4 h-4 mx-0.5 -mt-0.5 text-amber-400"/> menu button (3 dots, top of browser)</Step>
                <Step n={2}>Tap <b>"Install app"</b> or <b>"Add to Home screen"</b></Step>
                <Step n={3}>Confirm — done!</Step>
              </ol>
            ) : <Steps/>}
            {mode === "ios" && (
              <div className="text-[11px] text-zinc-400 italic pt-1">
                Note: iOS only allows this from Safari — not from in-app browsers like Instagram or Facebook.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
